<template>
  <div>
    <!-- Display mode - no gray bar, consistent with LiteralValue -->
    <div v-if="!edit">
      <div v-for="(pair, index) of pairs" :key="pair[0]" class="mb-1">
        <span class="d-inline-flex align-center">
          <v-icon small left color="secondary">{{
            index > 0 ? 'mdi-circle-small' : 'mdi-code-json'
          }}</v-icon>
          {{ pair[0] + ' → ' + pair[1] }}
          <copy-icon :value="String(pair[1])" />
        </span>
      </div>
    </div>

    <!-- Edit mode -->
    <div v-if="edit">
      <!-- JsonKeyValuePair components for each entry -->
      <json-key-value-pair
        v-for="(pair, index) in keyValuePairs"
        :key="pair.id"
        :key-value="pair.key"
        :value-value="pair.value"
        :existing-keys="allKeys"
        :original-key="pair.originalKey"
        :original-value="pair.originalValue"
        :is-last-entry="index === keyValuePairs.length - 1"
        @update:key="updatePairKey(index, $event)"
        @update:value="updatePairValue(index, $event)"
        @delete="deletePair(index)"
        @request-new-entry="addNewEntry"
      />

      <!-- Validation errors at property level -->
      <v-alert
        v-if="validationErrors.length > 0"
        type="error"
        dense
        text
        class="mt-2 mb-2"
      >
        <div v-for="error in validationErrors" :key="error">{{ error }}</div>
      </v-alert>
    </div>
  </div>
</template>

<script>
import { JsonLiteral } from '@/rec-cache/rec-cache-types'
import JsonKeyValuePair from './JsonKeyValuePair.vue'
import CopyIcon from './CopyIcon.vue'

export default {
  components: {
    JsonKeyValuePair,
    CopyIcon,
  },
  props: {
    jsonLiteral: {
      type: Object,
      default: () => new JsonLiteral({}),
    },
    edit: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    keyValuePairs: [],
    pairIdCounter: 0,
  }),
  computed: {
    jsonObject() {
      return this.jsonLiteral.value || {}
    },
    pairs() {
      return Object.entries(this.jsonObject)
    },
    allKeys() {
      return this.keyValuePairs.map((pair) => pair.key).filter((k) => k)
    },
    hasRealEntries() {
      return this.keyValuePairs.some((pair) => pair.key && pair.value)
    },
    validationErrors() {
      // Remove the incomplete entries validation since it's now handled by individual fields
      return []
    },
  },
  watch: {
    edit: {
      handler(newValue) {
        if (newValue) {
          this.initializeKeyValuePairs()
        }
      },
      immediate: true,
    },
    // jsonLiteral: {
    //   handler() {
    //     if (this.edit) {
    //       this.initializeKeyValuePairs()
    //     }
    //   },
    //   deep: true,
    // },
  },
  methods: {
    initializeKeyValuePairs() {
      // Convert current JSON object to key-value pairs with undo tracking
      const pairs = Object.entries(this.jsonObject).map(([key, value]) => ({
        id: this.generatePairId(),
        key,
        value,
        originalKey: key,
        originalValue: value,
      }))

      // Always add one empty line for new entries
      pairs.push({
        id: this.generatePairId(),
        key: '',
        value: '',
        originalKey: undefined,
        originalValue: undefined,
      })

      this.keyValuePairs = pairs
    },
    generatePairId() {
      return `pair-${this.pairIdCounter++}`
    },
    updatePairKey(index, newKey) {
      const pair = this.keyValuePairs[index]
      if (pair) {
        pair.key = newKey
        this.emitChange()
      }
    },
    updatePairValue(index, newValue) {
      const pair = this.keyValuePairs[index]
      if (pair) {
        pair.value = newValue
        this.emitChange()
      }
    },
    deletePair(index) {
      this.keyValuePairs.splice(index, 1)

      // If we removed all real entries, emit delete for whole property
      if (!this.hasRealEntries) {
        this.onDelete()
        return
      }

      this.emitChange()
    },
    addNewEntry() {
      // Only add if the last entry doesn't already exist or is not empty
      const lastPair = this.keyValuePairs[this.keyValuePairs.length - 1]
      if (lastPair && (lastPair.key || lastPair.value)) {
        this.keyValuePairs.push({
          id: this.generatePairId(),
          key: '',
          value: '',
          originalKey: undefined,
          originalValue: undefined,
        })
      }
    },
    emitChange() {
      // Only include pairs that have both key and value
      const validPairs = this.keyValuePairs.filter(
        (pair) => pair.key && pair.value
      )

      const updatedObject = {}
      validPairs.forEach((pair) => {
        updatedObject[pair.key] = pair.value
      })

      this.$emit('input', new JsonLiteral(updatedObject))
    },
    onDelete() {
      this.$emit('delete', this.jsonLiteral)
    },
    // Public method to check validation - can be called by parent
    isValid() {
      return this.validationErrors.length === 0
    },
  },
}
</script>

<style scoped>
/* Remove any custom spacing - let Vuetify handle it */
.mb-1 {
  margin-bottom: 4px !important;
}
</style>
